import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Building2, Plus, X, Upload, AlertTriangle, CheckCircle2, Shield, Trash2, FileText, ArrowLeft } from "lucide-react";

const ENTITY_TYPES = ["Limited", "LLP", "Partnership", "Other"];
const SERVICES = ["scaffolding", "edge protection", "containment", "labour only", "transport", "design"];
const DOC_PRESETS = [
  { category: "insurance", code: "EL", label: "Employer's Liability Insurance" },
  { category: "insurance", code: "PL", label: "Public Liability Insurance" },
  { category: "insurance", code: "PI", label: "Professional Indemnity" },
  { category: "insurance", code: "CAR", label: "Contractor's All-Risk" },
  { category: "accreditation", code: "CISRS", label: "CISRS (Company)" },
  { category: "accreditation", code: "CHAS", label: "CHAS" },
  { category: "accreditation", code: "SSIP", label: "SSIP" },
  { category: "accreditation", code: "NASC", label: "NASC" },
  { category: "accreditation", code: "ISO9001", label: "ISO 9001" },
  { category: "accreditation", code: "ISO14001", label: "ISO 14001" },
  { category: "accreditation", code: "ISO45001", label: "ISO 45001" },
  { category: "operational", code: "RAMS", label: "Standard RAMS on file" },
  { category: "operational", code: "HSPOL", label: "Health & Safety Policy" },
  { category: "operational", code: "TRMATRIX", label: "Training / Competency Matrix" },
];

const CLS_BADGE = {
  expired: { bg: "#fee2e2", fg: "#b91c1c", label: "EXPIRED" },
  expiring_7: { bg: "#fee2e2", fg: "#b91c1c", label: "<7 DAYS" },
  expiring_30: { bg: "#fef3c7", fg: "#92400e", label: "<30 DAYS" },
  expiring_60: { bg: "#fef3c7", fg: "#92400e", label: "<60 DAYS" },
  missing: { bg: "#f1f5f9", fg: "#334155", label: "MISSING" },
  ok: { bg: "#dcfce7", fg: "#065f46", label: "OK" },
};

function classify(exp) {
  if (!exp) return "missing";
  const d = new Date(exp + "T00:00:00Z");
  const today = new Date();
  const delta = Math.floor((d - today) / (1000 * 60 * 60 * 24));
  if (delta < 0) return "expired";
  if (delta <= 7) return "expiring_7";
  if (delta <= 30) return "expiring_30";
  if (delta <= 60) return "expiring_60";
  return "ok";
}

export default function Subcontractors() {
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showDoc, setShowDoc] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/subcontractor-companies"); setList(data); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const loadOne = async (id) => {
    try { const { data } = await api.get(`/subcontractor-companies/${id}`); setSelected(data); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const deleteDoc = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    try { await api.delete(`/subcontractor-companies/${selected.id}/documents/${docId}`); setSuccess("Document deleted"); loadOne(selected.id); load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (selected) {
    return <SubcontractorDetail company={selected} onBack={() => { setSelected(null); load(); }} onAddDoc={() => setShowDoc(true)} onDeleteDoc={deleteDoc} showDoc={showDoc} setShowDoc={setShowDoc} onDocAdded={() => { setShowDoc(false); setSuccess("Document added"); loadOne(selected.id); load(); }} onError={(m) => setError(m)} success={success} error={error} />;
  }

  return (
    <div data-testid="subcontractors-page" style={{ padding: "24px 24px 64px" }}>
      <header style={{ marginBottom: 20, display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div className="mono" style={{ color: "#ff5a00", fontWeight: 600, fontSize: 12, letterSpacing: "0.1em" }}>PHASE 2B · COMPANIES · COMPLIANCE GATES</div>
          <h1 style={{ fontSize: 28, fontWeight: 900, margin: "4px 0 0" }}>Subcontractor companies</h1>
          <div style={{ color: "#64748b", marginTop: 4 }}>Company records + insurance / accreditation / operational documents. EL or PL expired/missing → company is hard-blocked. CIS-individuals live on the operative record (pay basis = CIS-subbie).</div>
        </div>
        <button data-testid="new-subbie-btn" className="btn-primary" onClick={() => setShowNew(true)}><Plus size={16} style={{ marginRight: 6, verticalAlign: "middle" }} /> New company</button>
      </header>

      {error ? <Banner type="error" testid="subbie-error" msg={error} /> : null}
      {success ? <Banner type="success" testid="subbie-success" msg={success} /> : null}

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table className="responsive-table" data-testid="subbie-table">
          <thead>
            <tr>
              <th>Company</th>
              <th>Type</th>
              <th>Contact</th>
              <th>Services</th>
              <th>Operatives</th>
              <th>Compliance</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: "center", color: "#64748b" }}>No subcontractor companies yet. Click &quot;New company&quot; to add one.</td></tr>
            ) : list.map((c) => {
              const blocked = c.compliance?.critical_block;
              return (
                <tr key={c.id} data-testid={`subbie-row-${c.id}`} onClick={() => loadOne(c.id)} style={{ cursor: "pointer" }}>
                  <td data-label="Company"><Building2 size={14} style={{ verticalAlign: "middle", marginRight: 6, color: "#64748b" }} /><strong>{c.name}</strong></td>
                  <td data-label="Type" style={{ fontSize: 12, color: "#64748b" }}>{c.entity_type}</td>
                  <td data-label="Contact" style={{ fontSize: 12, color: "#64748b" }}>{c.contact_name}<br />{c.contact_email}</td>
                  <td data-label="Services" style={{ fontSize: 12, color: "#64748b" }}>{(c.services_offered || []).join(", ")}</td>
                  <td data-label="Operatives">{c.operative_count ?? "—"}</td>
                  <td data-label="Compliance">
                    {blocked ? (
                      <span data-testid={`subbie-blocked-${c.id}`} className="mono" style={{ background: "#fee2e2", color: "#b91c1c", padding: "2px 8px", borderRadius: 3, fontSize: 11, fontWeight: 700 }}>
                        <AlertTriangle size={11} style={{ marginRight: 4, verticalAlign: "middle" }} />BLOCKED
                      </span>
                    ) : (c.compliance?.expiring?.expiring_30 || c.compliance?.expiring?.expiring_60) ? (
                      <span className="mono" style={{ background: "#fef3c7", color: "#92400e", padding: "2px 8px", borderRadius: 3, fontSize: 11, fontWeight: 700 }}>EXPIRING SOON</span>
                    ) : (
                      <span className="mono" style={{ background: "#dcfce7", color: "#065f46", padding: "2px 8px", borderRadius: 3, fontSize: 11, fontWeight: 700 }}><CheckCircle2 size={11} style={{ marginRight: 4, verticalAlign: "middle" }} />OK</span>
                    )}
                  </td>
                  <td data-label="Status" style={{ fontSize: 12 }}>{c.status}</td>
                  <td><button data-testid={`open-subbie-${c.id}`} className="btn-ghost">Open</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showNew ? <NewSubbieModal onClose={() => setShowNew(false)} onCreated={(c) => { setShowNew(false); setSuccess(`Created ${c.name}`); load(); loadOne(c.id); }} onError={setError} /> : null}
    </div>
  );
}

function SubcontractorDetail({ company, onBack, onAddDoc, onDeleteDoc, showDoc, setShowDoc, onDocAdded, onError, success, error }) {
  return (
    <div data-testid="subbie-detail" style={{ padding: "24px 24px 64px" }}>
      <button data-testid="back-to-list" className="btn-ghost" onClick={onBack}><ArrowLeft size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />Back to companies</button>
      <header style={{ margin: "12px 0 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h1 style={{ fontSize: 28, fontWeight: 900, margin: 0 }}>{company.name}</h1>
          {company.compliance?.critical_block ? (
            <span className="mono" style={{ background: "#fee2e2", color: "#b91c1c", padding: "4px 10px", borderRadius: 3, fontSize: 12, fontWeight: 700 }}>
              <AlertTriangle size={12} style={{ marginRight: 4, verticalAlign: "middle" }} />HARD-BLOCKED
            </span>
          ) : null}
        </div>
        <div style={{ color: "#64748b", marginTop: 4 }}>{company.entity_type} · {company.contact_name} · {company.contact_email} · {company.contact_phone}</div>
      </header>

      {error ? <Banner type="error" testid="subbie-error" msg={error} /> : null}
      {success ? <Banner type="success" testid="subbie-success" msg={success} /> : null}

      {company.compliance?.reasons?.length ? (
        <div className="card" style={{ padding: 16, marginBottom: 16, background: "#fef2f2", borderLeft: "4px solid #dc2626" }}>
          <div className="mono" style={{ fontSize: 11, color: "#b91c1c", fontWeight: 700, marginBottom: 6 }}>WHY THIS COMPANY IS BLOCKED</div>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {company.compliance.reasons.map((r, i) => <li key={i} style={{ color: "#b91c1c" }}>{r}</li>)}
          </ul>
        </div>
      ) : null}

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 20 }}>
        <div className="card" style={{ padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900 }}><Shield size={16} style={{ verticalAlign: "middle", marginRight: 6 }} />Compliance documents</h2>
            <button data-testid="add-doc-btn" className="btn-primary" onClick={onAddDoc}><Plus size={14} style={{ marginRight: 4, verticalAlign: "middle" }} />Add document</button>
          </div>
          {(company.documents || []).length === 0 ? (
            <div style={{ color: "#64748b", textAlign: "center", padding: 24 }}>No documents on file yet.</div>
          ) : (
            <table className="responsive-table" data-testid="docs-table">
              <thead>
                <tr><th>Doc</th><th>Issuer</th><th>Policy #</th><th>Expiry</th><th>Status</th><th>File</th><th></th></tr>
              </thead>
              <tbody>
                {company.documents.map((d) => {
                  const cls = classify(d.expiry_date);
                  const badge = CLS_BADGE[cls];
                  return (
                    <tr key={d.id} data-testid={`doc-row-${d.id}`}>
                      <td data-label="Doc"><strong>{d.label}</strong><br /><span className="mono" style={{ fontSize: 10, color: "#64748b" }}>{d.category} · {d.code}</span></td>
                      <td data-label="Issuer">{d.issuer}</td>
                      <td data-label="Policy">{d.policy_number}</td>
                      <td data-label="Expiry">{d.expiry_date || <span style={{ color: "#94a3b8" }}>—</span>}</td>
                      <td data-label="Status"><span data-testid={`doc-status-${d.id}`} className="mono" style={{ background: badge.bg, color: badge.fg, padding: "2px 8px", borderRadius: 3, fontSize: 11, fontWeight: 700 }}>{badge.label}</span></td>
                      <td data-label="File">{d.file_url ? <a href={d.file_url} target="_blank" rel="noreferrer"><FileText size={14} /></a> : <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                      <td><button data-testid={`del-doc-${d.id}`} className="btn-ghost" onClick={() => onDeleteDoc(d.id)}><Trash2 size={12} color="#b91c1c" /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="card" style={{ padding: 16 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 900 }}>Operatives supplied</h2>
          <div style={{ color: "#64748b", fontSize: 12, marginTop: 4 }}>People sent to site by this company</div>
          {(company.operatives || []).length === 0 ? (
            <div style={{ color: "#64748b", textAlign: "center", padding: 16 }}>None yet.</div>
          ) : (
            <ul style={{ paddingLeft: 18, margin: "8px 0 0" }}>
              {company.operatives.map((o) => (
                <li key={o.id} data-testid={`subbie-op-${o.id}`} style={{ marginBottom: 4 }}>
                  {o.full_name} <span className="mono" style={{ fontSize: 10, color: "#64748b" }}>{o.pay_basis}</span>
                  {o.rtw_held ? null : <span className="mono" style={{ background: "#fee2e2", color: "#b91c1c", padding: "1px 5px", borderRadius: 2, fontSize: 9, marginLeft: 6 }}>RTW MISSING</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {showDoc ? <NewDocModal companyId={company.id} onClose={() => setShowDoc(false)} onCreated={onDocAdded} onError={onError} /> : null}
    </div>
  );
}

function NewSubbieModal({ onClose, onCreated, onError }) {
  const [form, setForm] = useState({ name: "", entity_type: "Limited", company_number: "", vat_number: "", contact_name: "", contact_email: "", contact_phone: "", address: "", notes: "" });
  const [services, setServices] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.name) { onError("Company name required"); return; }
    setBusy(true);
    try { const { data } = await api.post("/subcontractor-companies", { ...form, services_offered: services }); onCreated(data); }
    catch (e) { onError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="New subcontractor company" testid="new-subbie-modal" onClose={onClose}>
      <div className="mono" style={{ fontSize: 11, color: "#92400e", background: "#fef3c7", border: "1px solid #fde68a", padding: 8, borderRadius: 4, marginBottom: 12 }}>
        ⚠️ Companies only (Ltd / LLP / Partnership). A self-employed CIS individual supplying their own labour is <strong>not</strong> a company — add them on the People / Operatives page with pay basis = CIS-subbie.
      </div>
      <Field label="Company name *"><input data-testid="subbie-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="Entity type"><select data-testid="subbie-entity-type" value={form.entity_type} onChange={(e) => setForm({ ...form, entity_type: e.target.value })}>{ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></Field>
        <Field label="Company number"><input data-testid="subbie-company-no" value={form.company_number} onChange={(e) => setForm({ ...form, company_number: e.target.value })} /></Field>
        <Field label="Contact name"><input data-testid="subbie-contact-name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></Field>
        <Field label="Contact email"><input data-testid="subbie-contact-email" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></Field>
        <Field label="Phone"><input data-testid="subbie-phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></Field>
        <Field label="VAT number"><input data-testid="subbie-vat" value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} /></Field>
      </div>
      <Field label="Address"><textarea data-testid="subbie-address" rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
      <Field label="Services offered">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
          {SERVICES.map((s) => (
            <label key={s} style={{ padding: "4px 10px", background: services.includes(s) ? "#fef3c7" : "white", border: "1px solid #e2e8f0", borderRadius: 4, cursor: "pointer", fontSize: 12 }}>
              <input data-testid={`service-${s}`} type="checkbox" checked={services.includes(s)} onChange={() => setServices((cur) => cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s])} style={{ marginRight: 4 }} />{s}
            </label>
          ))}
        </div>
      </Field>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button data-testid="submit-new-subbie" className="btn-primary" onClick={submit} disabled={busy}>{busy ? "Creating…" : "Create"}</button>
      </div>
    </ModalShell>
  );
}

function NewDocModal({ companyId, onClose, onCreated, onError }) {
  const [preset, setPreset] = useState(DOC_PRESETS[0]);
  const [form, setForm] = useState({ issuer: "", policy_number: "", cover_amount: "", expiry_date: "", notes: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("category", preset.category);
      fd.append("code", preset.code);
      fd.append("label", preset.label);
      fd.append("issuer", form.issuer);
      fd.append("policy_number", form.policy_number);
      if (form.cover_amount) fd.append("cover_amount", form.cover_amount);
      if (form.expiry_date) fd.append("expiry_date", form.expiry_date);
      fd.append("notes", form.notes);
      if (file) fd.append("file", file);
      await api.post(`/subcontractor-companies/${companyId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onCreated();
    } catch (e) { onError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="Add compliance document" testid="new-doc-modal" onClose={onClose}>
      <Field label="Document type *">
        <select data-testid="doc-preset" value={preset.code} onChange={(e) => setPreset(DOC_PRESETS.find((p) => p.code === e.target.value))}>
          {DOC_PRESETS.map((p) => <option key={p.code} value={p.code}>{p.category} · {p.label}</option>)}
        </select>
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="Issuer / certifier"><input data-testid="doc-issuer" value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })} /></Field>
        <Field label="Policy / cert number"><input data-testid="doc-policy" value={form.policy_number} onChange={(e) => setForm({ ...form, policy_number: e.target.value })} /></Field>
        <Field label="Cover amount (£)"><input data-testid="doc-cover" type="number" value={form.cover_amount} onChange={(e) => setForm({ ...form, cover_amount: e.target.value })} /></Field>
        <Field label="Expiry date"><input data-testid="doc-expiry" type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} /></Field>
      </div>
      <Field label="File upload (PDF / JPG / PNG, max 8MB)">
        <input data-testid="doc-file" type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0])} />
        {file ? <div className="mono" style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>{file.name} · {(file.size / 1024).toFixed(0)} KB</div> : null}
      </Field>
      <Field label="Notes"><textarea data-testid="doc-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button data-testid="submit-new-doc" className="btn-primary" onClick={submit} disabled={busy}><Upload size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />{busy ? "Saving…" : "Save document"}</button>
      </div>
    </ModalShell>
  );
}

function Banner({ type, msg, testid }) {
  const bg = type === "error" ? "#fef2f2" : "#ecfdf5";
  const fg = type === "error" ? "#b91c1c" : "#065f46";
  const border = type === "error" ? "#dc2626" : "#059669";
  const Icon = type === "error" ? AlertTriangle : CheckCircle2;
  return <div data-testid={testid} className="card" style={{ padding: 12, marginBottom: 12, background: bg, color: fg, borderLeft: `4px solid ${border}`, display: "flex", gap: 8, alignItems: "center" }}><Icon size={16} /> {msg}</div>;
}

function ModalShell({ title, testid, onClose, children }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 80 }}>
      <div data-testid={testid} className="card" style={{ background: "white", maxWidth: 720, width: "100%", maxHeight: "90vh", overflowY: "auto", padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontWeight: 900 }}>{title}</h2>
          <button className="btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginTop: 8 }}>
      <div className="mono" style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>{label}</div>
      {children}
    </label>
  );
}
